import React, { useState, useRef, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Send, Loader, Sparkles, Plus, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { TodayHarvestBackdrop } from '@/components/LivingSky';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STARTER_PROMPTS = [
  "What should I sow this week?",
  "My tomato leaves are curling — what's wrong?",
  "How do I improve my clay soil?",
  "Best companion plants for brassicas?",
];

function relTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${Math.max(1, mins)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
}

const MD_COMPONENTS = {
  h1: ({ node, ...props }) => <h4 className="text-[#1A4331] font-bold text-base mt-4 mb-2 first:mt-0" {...props} />,
  h2: ({ node, ...props }) => <h4 className="text-[#1A4331] font-bold text-base mt-4 mb-2 first:mt-0" {...props} />,
  h3: ({ node, ...props }) => <h4 className="text-[#1A4331] font-bold text-sm mt-4 mb-1.5 first:mt-0 uppercase tracking-wider" {...props} />,
  p: ({ node, ...props }) => <p className="text-sm leading-relaxed my-2.5 first:mt-0 last:mb-0" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-[#1A4331] font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="text-[#7A6B57] italic" {...props} />,
  ul: ({ node, ...props }) => <ul className="my-2.5 space-y-1.5 list-none pl-0" {...props} />,
  ol: ({ node, ...props }) => <ol className="my-2.5 space-y-1.5 list-decimal pl-5" {...props} />,
  li: ({ node, ...props }) => (
    <li className="text-sm leading-relaxed pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-[#6B9F4A] before:font-bold" {...props} />
  ),
  code: ({ node, inline, ...props }) => inline
    ? <code className="bg-[#E5EBDD] text-[#1A4331] px-1.5 py-0.5 rounded text-xs font-mono" {...props} />
    : <code className="block bg-[#E5EBDD] text-[#1A4331] p-3 rounded-xl text-xs font-mono overflow-x-auto" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-[#C9A85B] pl-3 my-2 text-[#7A6B57] italic text-sm" {...props} />
  ),
  a: ({ node, ...props }) => <a className="text-[#6B9F4A] underline hover:text-[#1A4331]" target="_blank" rel="noopener noreferrer" {...props} />,
  hr: () => <hr className="my-3 border-[#1A4331]/10" />,
};

function AICoach({ user }) {
  const firstName = user?.name?.split(' ')[0] || 'there';
  const greeting = {
    role: 'assistant',
    content: `**Hello ${firstName}!** 🌱 I'm your AI garden coach. I can help you with:\n\n- What to plant now for your season\n- Crop care advice (watering, feeding, pest control)\n- Diagnosing plant problems\n- Harvest timing and storage tips\n- Soil health and companion planting\n\nWhat can I help you with today?`
  };
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([greeting]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = () => {
    fetch(`${API}/ai/conversations`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setConversations(d.conversations || []))
      .catch(() => {});
  };

  useEffect(() => { loadConversations(); }, []);

  const openConversation = async (convId) => {
    if (isLoading || convId === activeConvId) return;
    setActiveConvId(convId);
    try {
      const r = await fetch(`${API}/ai/conversations/${convId}/messages`, { credentials: 'include' });
      const d = await r.json();
      setMessages([greeting, ...(d.messages || []).map(m => ({ role: m.role, content: m.content }))]);
    } catch (e) { console.error(e); }
  };

  const newConversation = () => {
    if (isLoading) return;
    setActiveConvId(null);
    setMessages([greeting]);
  };

  const deleteConversation = async (e, convId) => {
    e.stopPropagation();
    try {
      await fetch(`${API}/ai/conversations/${convId}`, { method: 'DELETE', credentials: 'include' });
      if (convId === activeConvId) newConversation();
      loadConversations();
    } catch (err) { console.error(err); }
  };

  const send = async (text) => {
    if (!text.trim() || isLoading) return;
    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text, conversation_id: activeConvId })
      });

      const newConvId = response.headers.get('X-Conversation-Id');
      if (newConvId && newConvId !== activeConvId) setActiveConvId(newConvId);
      if (!response.ok || !response.body) throw new Error(`Chat request failed (${response.status})`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';
      let buffer = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop();
        for (const frame of frames) {
          if (!frame.startsWith('data: ')) continue;
          const payload = frame.slice(6);
          if (payload === '[DONE]') continue;
          try { assistantMessage += JSON.parse(payload); } catch (err) { assistantMessage += payload; }
        }
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: 'assistant', content: assistantMessage };
          return updated;
        });
      }
      loadConversations();
    } catch (e) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Sorry, I had trouble responding just now. Please try again."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); send(input); };

  const showStarters = messages.length === 1;

  return (
    <Layout user={user}>
      <TodayHarvestBackdrop />
      <div className="mb-6">
        <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          AI Garden Coach
        </h1>
        <p className="text-base text-[#7A6B57] mt-2">Ask me anything about growing vegetables</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-4 items-start">
        {/* Conversations sidebar */}
        <div className="bg-[#FAF6EC] rounded-3xl border border-[#1A4331]/10 p-4 lg:sticky lg:top-6" data-testid="conversations-sidebar">
          <button onClick={newConversation} data-testid="new-conversation-button"
            className="w-full bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 px-4 rounded-full text-sm flex items-center justify-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> New conversation
          </button>
          <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#7A6B57] mt-5 mb-2 px-1">Recent</p>
          {conversations.length === 0 ? (
            <p className="text-sm text-[#7A6B57] italic px-1" data-testid="no-conversations">No past conversations yet.</p>
          ) : (
            <div className="space-y-1 max-h-[420px] overflow-y-auto" data-testid="conversations-list">
              {conversations.map(c => (
                <div key={c.id} onClick={() => openConversation(c.id)} data-testid={`conversation-${c.id}`}
                  className={`group flex items-start gap-2 px-3 py-2.5 rounded-2xl cursor-pointer transition-colors ${c.id === activeConvId ? 'bg-[#1A4331]/8 border border-[#1A4331]/15' : 'hover:bg-white border border-transparent'}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1A4331] truncate">{c.title}</p>
                    <p className="text-[10px] text-[#7A6B57] mt-0.5">{relTime(c.updated_at)}</p>
                  </div>
                  <button onClick={(e) => deleteConversation(e, c.id)} data-testid={`delete-conversation-${c.id}`}
                    className="opacity-0 group-hover:opacity-100 text-[#7A6B57] hover:text-[#C66B4A] transition-opacity mt-0.5">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat panel */}
        <div className="bg-[#FAF6EC] rounded-3xl border border-[#1A4331]/10 shadow-sm overflow-hidden">
          <div className="h-[560px] overflow-y-auto p-4 sm:p-6 space-y-5" data-testid="chat-messages">
            {messages.map((message, idx) => (
              <MessageBubble key={idx} idx={idx} message={message} />
            ))}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex justify-start" data-testid="typing-indicator">
                <div className="flex items-center gap-2 bg-white border border-[#1A4331]/10 rounded-2xl px-4 py-3">
                  <Loader className="w-4 h-4 text-[#6B9F4A] animate-spin" />
                  <span className="text-xs text-[#7A6B57]">Thinking…</span>
                </div>
              </div>
            )}
            {showStarters && (
              <div className="mt-6" data-testid="starter-prompts">
                <p className="text-xs font-bold tracking-wider uppercase text-[#7A6B57] mb-3">Try asking</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {STARTER_PROMPTS.map((p, i) => (
                    <button key={i} onClick={() => send(p)} data-testid={`starter-${i}`}
                      className="text-left text-sm bg-white hover:bg-[#D9E1CB] border border-[#1A4331]/10 text-[#1A4331] px-4 py-3 rounded-2xl transition-colors">
                      {p}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="border-t border-[#1A4331]/10 p-3 sm:p-4 bg-white/40">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about planting, pests, watering, soil…"
                disabled={isLoading}
                data-testid="chat-input"
                className="flex-1 px-4 py-3 bg-white border border-[#1A4331]/10 focus:border-[#1A4331] rounded-full outline-none transition-all disabled:opacity-50 text-sm"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                data-testid="send-message-button"
                className="bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] p-3 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
              >
                {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-[#7A6B57] mt-2 text-center tracking-wider uppercase">
              Homegrown AI · advice adapts to your climate, crops, weather & growing style
            </p>
          </form>
        </div>
      </div>
    </Layout>
  );
}

/** Chat message bubble — proper markdown, tight styling. */
function MessageBubble({ idx, message }) {
  const isUser = message.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end" data-testid={`message-${idx}`}>
        <div className="max-w-[85%] bg-[#1A4331] text-[#FAF6EC] rounded-3xl rounded-br-lg px-5 py-3">
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start gap-2" data-testid={`message-${idx}`}>
      <div className="w-8 h-8 rounded-full bg-[#1A4331] flex items-center justify-center flex-shrink-0 mt-1">
        <Sparkles className="w-4 h-4 text-[#C9A85B]" />
      </div>
      <div className="max-w-[85%] bg-white border border-[#1A4331]/10 text-[#2A3528] rounded-3xl rounded-tl-lg px-5 py-4">
        <div className="prose prose-sm max-w-none coach-md">
          <ReactMarkdown components={MD_COMPONENTS}>
            {message.content || '\u00A0'}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

export default AICoach;
